import ConfirmationDialog from "@Components/app/common/confirmation-dialog";
import Icon from "@Components/generic/icon";
import { Actions } from "@Context/common/general/context";
import { AnalyticsEvents } from "@Enums";
import AccountRoles from "@Enums/AccountRoles";
import { faUserSlash } from "@fortawesome/pro-solid-svg-icons";
import { useGlobalContext } from "@hooks/index";
import useI18n from "@i18n";
import { Alert, Button, CircularProgress, Stack, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { Parm0Callback } from "../members/teamInterfaces";

const NoSubscriptionsMessage = ({
  open,
  onSelectPlan,
  onSelectAccount,
  onLogout,
  onRefresh,
  isUnityEmbedded,
}: {
  open: boolean;
  onSelectPlan: Parm0Callback;
  onSelectAccount: Parm0Callback;
  onLogout: Parm0Callback;
  onRefresh: () => Promise<void>;
  isUnityEmbedded: boolean;
}) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const { globalState, dispatch } = useGlobalContext();
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const shouldAllowRefresh = Boolean(globalState.user.id) && Boolean(globalState.user.noAccount);

  useEffect(() => {
    // Send no subscriptions event
    dispatch({
      type: Actions.AppActions.AnalyticsEvent,
      payload: {
        type: AnalyticsEvents.no_subscriptions_notification,
      },
    });
  }, []);

  const handleRefreshClick = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setIsRefreshing(false);
  };

  return (
    <ConfirmationDialog
      open={open}
      dialogProps={{
        open,
        disableEscapeKeyDown: true,
      }}
      iconProps={{
        icon: faUserSlash,
        size: "4x",
        sx: {
          padding: theme => theme.spacing(2),
          color: theme => theme.palette.dsNeutral.v300,
        },
      }}
      title="No Active Subscriptions"
      message={
        isUnityEmbedded
          ? "Please change your account to continue or purchase a subscription plan at www.beyourbest.com"
          : "The selected account has no active subscriptions. Change account or purchase a subscription plan to continue"
      }
      disableConfirm={true}
      disableCancel={true}
      buttonStackProps={{
        paddingTop: 0,
        direction: "column",
      }}
      buttonProps={[
        {
          variant: "outlined",
        },
        {
          variant: "text",
        },
      ]}
    >
      <>
        {shouldAllowRefresh && <Alert severity="info">{i18n("notifications.subscription.info.message")}</Alert>}

        <Stack spacing={2} sx={{ pt: 4 }}>
          {!isUnityEmbedded && (globalState.account.id === null || globalState.account.user_role === AccountRoles.ACCOUNT_OWNER) && (
            <Button
              fullWidth
              disabled={isRefreshing}
              onClick={onSelectPlan}
              startIcon={<Icon dsIcon="creditcard" color={theme.palette.dsBase.v100} />}
            >
              {i18n("select-plan")}
            </Button>
          )}

          {shouldAllowRefresh && (
            <Button
              fullWidth
              variant="outlined"
              onClick={handleRefreshClick}
              disabled={isRefreshing}
              startIcon={
                isRefreshing ? (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: "inherit",
                      left: "16px",
                    }}
                  />
                ) : (
                  <Icon dsIcon="refresh" />
                )
              }
            >
              {i18n("button.refresh.subscriptions")}
            </Button>
          )}

          {globalState.account.accounts_data && globalState.account.accounts_data.length > 1 && (
            <Button fullWidth variant="outlined" disabled={isRefreshing} onClick={onSelectAccount} startIcon={<Icon dsIcon="account" />}>
              {i18n("button.change.account")}
            </Button>
          )}

          <Button fullWidth variant="outlined" onClick={onLogout} disabled={isRefreshing} startIcon={<Icon dsIcon="logOut" />}>
            {i18n("sign-out")}
          </Button>
        </Stack>
      </>
    </ConfirmationDialog>
  );
};

export default NoSubscriptionsMessage;
